import Chip from "@/components/Chip";
import SimpleTable from "@/components/SimpleTable";
import { Button } from "@/components/ui/button";
import { KEYS } from "@/lib/tourHelper";
import { AnswerSubCategory } from "@/schema/app/assessment/answerSubCategory";
import { format } from "date-fns";
import { useTranslation } from "react-i18next";
import { useNavigate } from "react-router-dom";

interface OverviewProps {
  data: AnswerSubCategory[];
}

const OverviewTable = ({ data }: OverviewProps) => {
  const { t } = useTranslation();
  const navigate = useNavigate();

  return (
    <div data-tour={KEYS.CATEGORY.SUB_CATEGORY_LIST}>
      <SimpleTable
        data={data}
        columns={[
          {
            key: "name",
            label: t("sub-category-app-label"),
            render: ({ content, question_count }) => (
              <div className="flex-col">
                <div className="font-medium">{content}</div>
                <div className="text-cool-gray-600">
                  {question_count} {t("question-counter")}
                </div>
              </div>
            ),
          },
          {
            key: "lastUpadated",
            label: t("last-updated"),
            width: "150px",
            render: ({ updated_at }) =>
              updated_at
                ? format(updated_at, t("date-time-format"))
                : t("not-answered-yet"),
          },
          {
            key: "status",
            label: t("status"),
            width: "150px",
            render: ({ is_answered }) =>
              is_answered ? (
                <Chip label={t("finished")} />
              ) : (
                <Chip variant="warning" label={t("unfinished")} />
              ),
          },
          {
            key: "action",
            label: t("action"),
            width: "150px",
            render: ({ id, is_answered }, index) =>
              index === 0 ? (
                <Button
                  data-tour={KEYS.CATEGORY.ANSWER_BUTTON}
                  onClick={() => navigate(`sub-categories/${id}`)}
                >
                  {is_answered ? t("review") : t("answer")}
                </Button>
              ) : (
                <Button onClick={() => navigate(`sub-categories/${id}`)}>
                  {is_answered ? t("review") : t("answer")}
                </Button>
              ),
          },
        ]}
      />
    </div>
  );
};

function Overview({ data }: OverviewProps) {
  const { t } = useTranslation();
  const isFinished = data.every(({ is_answered }) => is_answered);
  return (
    <div>
      <div className="text-lg font-bold">{t("answer-status")}</div>
      <div className="mb-6 mt-2">
        {t("status")}: {isFinished ? t("finished") : t("unfinished")}
      </div>
      <OverviewTable data={data} />
    </div>
  );
}

export default Overview;
